import React, {useState} from 'react';
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Button from "components/Button";
import {CheckCircleIcon, PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/solid";
import {LessonPlan, LessonPlanData} from "types/LessonPlan";
import {useNotifications} from "context/NotificationContext";
import DeleteLessonPlan from "pages/LessonPlan/Delete";
import {invalidate} from "services/query-client";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import Gallery from "components/Gallery";
import {ClipboardIcon, GlobeIcon} from "@heroicons/react/outline";
import {isUrl, truncate} from "services/string";
import StackedList from 'components/StackedList';
import StackedListItem from 'components/StackedListItem';
import Chip from "components/Chip";
import DescriptionListItem from "components/DescriptionListItem";
import {useOperator} from "context/OperatorContext";

function ViewLessonPlan() {
    const {id} = useParams()
    const {data: lessonPlan, isLoading} = useQuery(
        ['lesson-plans', {id}],
        () => ngage.lessonPlans.get(parseInt(id as string))
    )
    const update = useMutation((data: LessonPlanData) => ngage.lessonPlans.update(parseInt(id as string), data))

    const [deletingLessonPlan, setDeletingLessonPlan] = useState<LessonPlan | null>(null);

    const {termFor} = useOperator()
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prepare action handlers
    function handleDeleteLessonPlan() {
        if(lessonPlan) setDeletingLessonPlan(lessonPlan)
    }

    function handleEditLessonPlan() {
        if(id) navigate(`/lesson-plans/${id}/edit`)
    }

    async function handlePublishLessonPlan() {
        if(!lessonPlan) {
            return
        }

        const publishing = !lessonPlan.is_published

        try{
            await update.mutateAsync({is_published: publishing})
            await invalidate(['lesson-plans', {id}])

            if(publishing){
                notify("success", "Lesson Plan Published", `${lessonPlan.name} has been published and can now be used`)
            } else {
                notify("success", "Lesson Plan Unpublished", `${lessonPlan.name} has been unpublished and cannot be used`)
            }
        } catch (error) {
            notify("error", "Lesson Plan Not Updated", `${lessonPlan.name} publish status could not be changed`)
        }
    }

    // Prepare page actions
    const actions = <>
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteLessonPlan}>Delete</Button>
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditLessonPlan}>Edit</Button>
        <Button color={Button.colors.PRIMARY} icon={lessonPlan?.is_published? XCircleIcon : CheckCircleIcon} onClick={handlePublishLessonPlan} showSpinner={update.isLoading}>
            {lessonPlan?.is_published? 'Unpublish' : 'Publish'}
        </Button>
    </>

    return (
        <Page title={lessonPlan?.name || "View Lesson Plan"}
              breadcrumbs={{'Education': '/', 'Lesson Plans': '/lesson-plans', [lessonPlan?.name || 'View Lesson Plan']: `/lesson-plans/${id}`}}
              actions={actions}
              isLoading={isLoading}>

            {lessonPlan?
                <>
                    {/* Lesson Information */}
                    <section>
                        <Card
                            heading="Lesson Information"
                            subheading={`${lessonPlan.activities.length} activities / ${lessonPlan.duration.toHuman()}`}
                        >
                            <DescriptionList>
                                <div className="sm:col-span-1">
                                    <DescriptionTerm>Target Audience</DescriptionTerm>
                                    <DescriptionDetails>{lessonPlan.target_audience}</DescriptionDetails>
                                </div>

                                <div className="sm:col-span-2">
                                    <DescriptionTerm>Description</DescriptionTerm>
                                    <DescriptionDetails>
                                        <p className="whitespace-pre-line">{lessonPlan.description}</p>
                                    </DescriptionDetails>
                                </div>

                                <div className="sm:col-span-1">
                                    <DescriptionTerm>Learning Outcomes</DescriptionTerm>
                                    <DescriptionDetails>
                                        <ul className="list-disc list-inside">
                                            {lessonPlan.outcomes.map((outcome, index) => (
                                                <li key={index}>{outcome}</li>
                                            ))}
                                        </ul>
                                    </DescriptionDetails>
                                </div>

                                <div className="sm:col-span-1">
                                    <DescriptionTerm>Transferable Skills</DescriptionTerm>
                                    <DescriptionDetails>
                                        <ul className="list-disc list-inside">
                                            {lessonPlan.skills.map((skill, index) => (
                                                <li key={index}>{skill}</li>
                                            ))}
                                        </ul>
                                    </DescriptionDetails>
                                </div>
                            </DescriptionList>
                        </Card>
                    </section>

                    {/* Activities */}
                    <section className="mt-4">
                        {lessonPlan?.activities.map((activity, index) => (
                            <div className="mb-4" key={activity.id}>
                                <Card heading={`Activity ${index + 1}: ${activity.name}`} subheading={`${activity.duration.toHuman()}`}>
                                    <DescriptionList>

                                        {/* Description */}
                                        <DescriptionListItem span={2}>
                                            <DescriptionTerm>Description</DescriptionTerm>
                                            <DescriptionDetails>
                                                <p className="whitespace-pre-line">{activity.description}</p>
                                            </DescriptionDetails>
                                        </DescriptionListItem>

                                        {/* Media */}
                                        {activity.media.length > 0?
                                            <DescriptionListItem span={2}>
                                                <DescriptionTerm>Media</DescriptionTerm>
                                                <DescriptionDetails>
                                                    <Gallery media={activity.media} />
                                                </DescriptionDetails>
                                            </DescriptionListItem>
                                        : null}

                                        {/* Glossary */}
                                        {activity.glossary.length > 0?
                                            <DescriptionListItem span={2}>
                                                <DescriptionTerm>Glossary</DescriptionTerm>
                                                <DescriptionDetails>
                                                    <StackedList>
                                                        {activity.glossary.map((entry, index) => (
                                                            <StackedListItem key={index}>
                                                                <span className="block flex-shrink-0 font-semibold">
                                                                    "{entry.word}"
                                                                </span>
                                                                <span className="ml-2 block text-gray-500">
                                                                    {entry.definition}
                                                                </span>
                                                            </StackedListItem>
                                                        ))}
                                                    </StackedList>
                                                </DescriptionDetails>
                                            </DescriptionListItem>
                                        : null}

                                        {/* Resources */}
                                        {activity.resources.length > 0?
                                            <DescriptionListItem span={2}>
                                                <DescriptionTerm>Resources</DescriptionTerm>
                                                <DescriptionDetails>
                                                    <StackedList>

                                                        {activity.resources.map((resource, index) => (
                                                            <StackedListItem
                                                                key={index}
                                                                icon={isUrl(resource.value)? GlobeIcon : ClipboardIcon}
                                                                action={isUrl(resource.value)? <a href={resource.value} target="_blank" rel="noreferrer" className="font-medium text-brand hover:text-brand/80">View</a> : null}
                                                            >
                                                                <span className="flex-1 w-0 truncate font-semibold">
                                                                    {resource.name}
                                                                </span>
                                                                <span className="ml-2 flex-1 w-0 text-gray-500">
                                                                    {truncate(resource.value, 100)}
                                                                </span>
                                                            </StackedListItem>
                                                        ))}

                                                    </StackedList>
                                                </DescriptionDetails>
                                            </DescriptionListItem>
                                        : null}

                                        {/* Exhibits */}
                                        {activity.exhibits.length > 0?
                                            <DescriptionListItem span={2}>
                                                <DescriptionTerm>{termFor('Exhibits')}</DescriptionTerm>
                                                <DescriptionDetails>
                                                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {activity.exhibits.map((exhibit, index) => (
                                                            <li key={index}>
                                                                <Chip
                                                                    name={exhibit.name}
                                                                    description={exhibit.type.name}
                                                                    href={`/exhibits/${exhibit.id}`}
                                                                    backgroundSrc={exhibit.media[0]?.preview_url}
                                                                />
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </DescriptionDetails>
                                            </DescriptionListItem>
                                        : null}

                                    </DescriptionList>
                                </Card>
                            </div>
                        ))}
                    </section>
                </>
            : null}

            {/* Delete confirmation modal */}
            <DeleteLessonPlan
                lessonPlan={deletingLessonPlan}
                show={!!deletingLessonPlan}
                onCancel={() => setDeletingLessonPlan(null)}
                onDelete={lessonPlan => {
                    navigate('/lesson-plans')
                    notify("success", "Lesson Plan Deleted", `${lessonPlan.name} has been permanently removed`)
                }}
                onDeleteFail={lessonPlan => {
                    setDeletingLessonPlan(null)
                    notify("error", "Lesson Plan Not Deleted", `${lessonPlan.name} could not be removed`)
                }}
            />
        </Page>
    );
}

export default ViewLessonPlan;